import { Component, Input, OnInit, ChangeDetectorRef, OnDestroy, HostBinding, ElementRef, Renderer2, HostListener } from "@angular/core";
import { Router, NavigationEnd, IsActiveMatchOptions, ActivatedRoute } from "@angular/router";
import { trigger, state, style, transition, animate } from "@angular/animations";

import { debounceTime, map, of, Subscription, switchMap, timer } from "rxjs";
import { filter } from "rxjs/operators";

import { UltimaComponent } from "./ultima.component";
import { IMainMenuItem, MainMenuService } from "@main/menu-designer";
import { ProjectSettings, SystemConfigurationService } from "@main/system-configuration";
import { PatientService } from "../patient-switch/patient.service";
import { EnhancedRouterService } from "@mipro/services/enhanced-router.service";

@Component({
    selector: "[ultima-menu-item]",
    templateUrl: "./ultima-menu-item.component.html",
    styles: [`
        .nhs-icon-size {
            font-size: 14px !important;
            font-family: Inter, Roboto, Helvetica Neue Light, Helvetica Neue, Helvetica, Arial, Lucida Grande, sans-serif !important;
        }

        :host > a {
            touch-action: manipulation;
            cursor: pointer;
        }
    `],
    animations: [
        trigger("children", [
            state(
                "void",
                style({
                    height: "0px"
                })
            ),
            state(
                "hiddenAnimated",
                style({
                    height: "0px"
                })
            ),
            state(
                "visibleAnimated",
                style({
                    height: "*"
                })
            ),
            state(
                "visible",
                style({
                    height: "*",
                    "z-index": 100
                })
            ),
            state(
                "hidden",
                style({
                    height: "0px",
                    "z-index": "*"
                })
            ),
            transition("visibleAnimated => hiddenAnimated", animate("400ms cubic-bezier(0.86, 0, 0.07, 1)")),
            transition("hiddenAnimated => visibleAnimated", animate("400ms cubic-bezier(0.86, 0, 0.07, 1)")),
            transition("void => visibleAnimated, visibleAnimated => void", animate("400ms cubic-bezier(0.86, 0, 0.07, 1)"))
        ])
    ]
})
export class UltimaMenuItemComponent implements OnInit, OnDestroy {
    @Input() item: IMainMenuItem;
    @Input() index: number;
    @Input() root: boolean;
    @Input() parentKey: string;

    @HostBinding("class.active-menuitem")
    active = false;
    key: string;

    private readonly menuSourceSubscription: Subscription;
    private readonly menuResetSubscription: Subscription;
    private unlistenMouseEnter?: () => void;
    private touchStartX = 0;
    private touchStartY = 0;

    constructor(
        public ultimaMain: UltimaComponent,
        private router: Router,
        private route: ActivatedRoute,
        private enhancedRouterService: EnhancedRouterService,
        private cd: ChangeDetectorRef,
        private menuService: MainMenuService,
        private systemConfigurationService: SystemConfigurationService,
        private patientService: PatientService,
        private el: ElementRef<HTMLElement>,
        private renderer: Renderer2
    ) {
        this.menuSourceSubscription = this.menuService.menuSource$.subscribe(key => {
            // Deactivate current active menu
            if (this.active && this.key !== key && key.indexOf(this.key) !== 0) {
                this.active = false;
            }
        });

        this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
            this.active = false;
        });

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            switchMap(event => {
                // Only apply delay if force navigating
                if (this.enhancedRouterService.isForceNavigating) {
                    return timer(100).pipe(map(() => event));
                }
                return of(event);
            })
        ).subscribe(params => {
            if (this.ultimaMain.isHorizontal || this.ultimaMain.isSlim) {
                this.active = false;
            } else {
                this.updateActiveStateFromRoute();
            }
        });
    }

    ngOnInit() {
        if (!this.ultimaMain.isHorizontal || !this.ultimaMain.isSlim) {
            this.updateActiveStateFromRoute();
        }

        this.key = this.parentKey ? this.parentKey + "-" + this.index : String(this.index);

        if (!this.isCoarsePointer()) {
            this.unlistenMouseEnter = this.renderer.listen(this.el.nativeElement, "mouseenter", () => this.onMouseEnter());
        }
    }

    ngOnDestroy() {
        this.unlistenMouseEnter?.();

        if (this.menuSourceSubscription) {
            this.menuSourceSubscription.unsubscribe();
        }

        if (this.menuResetSubscription) {
            this.menuResetSubscription.unsubscribe();
        }
    }

    @HostListener("touchstart", ["$event"])
    onItemTouchStart(event: TouchEvent) {
        this.touchStartX = event.touches[0].clientX;
        this.touchStartY = event.touches[0].clientY;
    }

    @HostListener("touchend", ["$event"])
    onItemTouchEnd(event: TouchEvent) {
        const link = this.itemLink;
        if (!link || !link.contains(event.target as Node)) {
            return;
        }

        const touch = event.changedTouches[0];
        if (
            Math.abs(touch.clientX - this.touchStartX) > 10 ||
            Math.abs(touch.clientY - this.touchStartY) > 10
        ) {
            return;
        }

        // iOS: skip the delayed mouseenter/click sequence and fire click on the first tap.
        event.preventDefault();
        event.stopPropagation();
        link.click();
    }

    updateActiveStateFromRoute(): void {
        const formInstancesAllLink = "/app/formio/form-instances-all";

        if (this.item.routerLink == "/app") {
            this.active = location.pathname == "/app";
        } else if (this.item.routerLink == formInstancesAllLink || this.item.children?.some(child => child.routerLink == formInstancesAllLink)) {
            const params = this.route.snapshot.queryParams;
            const hasQueryParams = Object.keys(params).length > 0;

            if (hasQueryParams) {
                this.active = this.isItemActive(this.item, "exact");
            } else {
                this.active = this.isItemActive(this.item);
            }
        } else {
            this.active = this.isItemActive(this.item);
        }
    }

    isItemActive(item: IMainMenuItem, matchQueryParams:  "exact" | "subset" | "ignored" = "subset"): boolean {
        let result = false;

        if (item.children?.length) {
            item.children.forEach(x => (result = result || this.isItemActive(x, matchQueryParams)));
        } else {
            if (item.routerLink ?? item.referenceLink) {
                let linkToCheck = item.routerLink ?? item.referenceLink;
                linkToCheck = this.prepareUrlForComparison(linkToCheck);

                /** https://angular.io/api/router/Router#isactive */
                const isActive = this.router.isActive(linkToCheck, {
                    paths: "subset",
                    queryParams: matchQueryParams,
                    fragment: "ignored",
                    matrixParams: "ignored"
                });

                result = result || isActive;
            }
        }

        return result;
    }

    prepareUrlForComparison(url: string): string {
        return url.replace(/ /g, "%20")
            .replace(/\(/g, "%28")
            .replace(/\)/g, "%29");
    }

    shouldShowRouterItem(): boolean {
        return !!this.item.routerLink;
    }

    shouldShowHrefItem(): boolean {
        return this.item.href && !this.item.routerLink;
    }

    shouldShowLabelItem(): boolean {
        return !this.item.href && !this.item.routerLink && !this.item.customHandler && !!this.item.children?.length;
    }

    shouldShowCustomHandledItem(): boolean {
        return !!this.item.customHandler;
    }

    onMouseEnter() {
        // Activate item on hover
        if (
            this.root &&
            this.ultimaMain.menuHoverActive &&
            (this.ultimaMain.isHorizontal || this.ultimaMain.isSlim) &&
            this.ultimaMain.isDesktop
        ) {
            this.menuService.onMenuStateChange(this.key);
            this.active = true;
        }
    }

    itemClick(event: Event) {
        if (!!this.item.routerLink || this.item.href) {
            return true;
        }

        event.preventDefault();

        // Avoid processing disabled items
        if (this.item.disabled) {
            return true;
        }

        // Navigate with hover in horizontal mode
        if (this.root) {
            this.ultimaMain.menuHoverActive = !this.ultimaMain.menuHoverActive;
        }

        // Notify other items
        this.menuService.onMenuStateChange(this.key);

        // Execute command
        if (this.item.customHandler) {
            this.applyCustomHandler(this.item);
            return true;
        }

        // Toggle active state
        if (this.item.children) {
            this.active = !this.active;
        } else {
            // Activate item
            this.active = true;

            // Reset horizontal menu
            if (this.ultimaMain.isHorizontal || this.ultimaMain.isSlim) {
                this.menuService.reset();
            }

            // Hide overlay menus
            this.ultimaMain.collapsedMenuActive = false;
            this.ultimaMain.staticMenuMobileActive = false;
            this.ultimaMain.menuHoverActive = !this.ultimaMain.menuHoverActive;
        }
    }

    private applyCustomHandler(item: IMainMenuItem): void {
        if (!item.customHandler) {
            throw new Error("This item does not assumes custom handling.");
        }

        switch (item.customHandler) {
            case "theme":
                const theme = ProjectSettings.GetThemeByName(item.label);
                if (theme) {
                    this.systemConfigurationService.setThemeCode(theme.code);
                } else {
                    throw new Error(`The theme with label "${item.label}" not found.`);
                }
                break;
            case "click":
                item.onClick();
                break;
        }
    }

    onChildItemClick(){
        this.patientService.setPatientSelectionLock(false);

    }
    onRouterLinkClick(){
        this.patientService.setPatientSelectionLock(false);
    }

    private get itemLink(): HTMLAnchorElement | null {
        return this.el.nativeElement.querySelector(":scope > a");
    }

    private isCoarsePointer(): boolean {
        return typeof window !== "undefined" && window.matchMedia("(any-pointer: coarse)").matches;
    }
}
